import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import * as S from './AddToPremiumModal.styles';
import { Modal } from '@/components/UI/Modal';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { Input } from '@/components/UI/Input/Input';
import { Button } from '@/components/UI/Button/Button';
import { CheckAdExistenceResponse } from '@/api/types/ads.types';
import { CHECK_AD_EXISTENCE } from '@/api/queries/ads.queries';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { CommonTestIds } from '@/__e2e__/testIds/common';

interface AddToPremiumModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddToPremiumModal: FC<AddToPremiumModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [adNumber, setAdNumber] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const customPush = useCustomPush();

  const { refetch: checkAdExistence } = useQuery<CheckAdExistenceResponse>(
    CHECK_AD_EXISTENCE,
    {
      skip: true,
      errorPolicy: 'all',
    }
  );

  async function handleButtonClick() {
    setLoading(true);
    const { data } = await checkAdExistence({
      legacyId: Number(adNumber),
    });

    const adPath = data?.adExistence;

    if (adPath) {
      customPush(`${adPath}#modal_feature`);
    } else {
      setError(t('pages.ad.nothing_found_ad_description'));
      setLoading(false);
    }
  }

  function handleChangeInputValue(value: string) {
    setError('');
    setAdNumber(value);
  }

  function handleClose() {
    setAdNumber('');
    setError('');
    setLoading(false);
    onClose();
  }

  return (
    <Modal
      dataTestId={CommonTestIds.addToPremium.modal}
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth={600}
    >
      <S.Header>
        <IconBox
          $filename="crown.svg"
          $width={24}
          $height={20}
          $iconWidth={24}
          $iconHeight={20}
        />
        <S.Title>{t('pages.ad.make_premium')}</S.Title>
        <S.CloseButton onClick={handleClose}>
          <CloseIcon />
        </S.CloseButton>
      </S.Header>
      <S.Content>
        <S.Subtitle>{t('pages.ad.make_premium_subtitle')}</S.Subtitle>
        <Input
          isNumber
          inputMode="numeric"
          autoCorrect="off"
          spellCheck="false"
          autoComplete="off"
          autoCapitalize="none"
          autoFocus
          appearance="filled"
          value={adNumber}
          hasError={!!error}
          clear={() => handleChangeInputValue('')}
          helperText={error}
          label={t('pages.ad.ad_number')}
          onChange={value => handleChangeInputValue(value as string)}
        />
      </S.Content>
      <S.ActionWrapper>
        <Button
          isFullWidth
          appearance="primary"
          borderRadius={14}
          disabled={!adNumber || isLoading}
          onClick={handleButtonClick}
        >
          {t('pages.ad.make_premium')}
        </Button>
      </S.ActionWrapper>
    </Modal>
  );
};

AddToPremiumModal.displayName = 'AddToPremiumModal';
